import { useRef, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useClickOutside } from '../../lib/useClickOutside';
import { normalizeSurveySteps } from '../../lib/surveySteps';
import type { SurveyLinkMode, SurveyStep } from '../../types/survey';
import {
  ADD_QUESTION_TYPES,
  cloneStep,
  makeContentStep,
  makeQuestionStep,
  type AddableQuestionType,
} from './stepFactory';
import { LockedStepCard, SortableStepCard } from './StepCard';

// Phase 6B B3 — the reorderable step list between the intro + outro editors.
// The auto-inserted name step (per_location) renders locked at the top, outside
// the sortable zone; the rest reorder via dnd-kit. Add / duplicate / delete all
// route through normalizeSurveySteps so the name-step invariant holds. Editing a
// step opens the per-type editor (onEditStep → wired in B4).

function isAutoName(s: SurveyStep): boolean {
  return s.kind === 'question' && s.isAutoInserted;
}

export function StepList({
  steps,
  linkMode,
  onChange,
  onEditStep,
}: {
  steps: SurveyStep[];
  linkMode: SurveyLinkMode;
  onChange: (next: SurveyStep[]) => void;
  onEditStep: (id: string) => void;
}) {
  const autoName = steps.find(isAutoName) ?? null;
  const rest = steps.filter((s) => !isAutoName(s));
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const commit = (next: SurveyStep[]) => onChange(normalizeSurveySteps(next, linkMode));

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = rest.findIndex((s) => s.id === active.id);
    const newIndex = rest.findIndex((s) => s.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const newRest = arrayMove(rest, oldIndex, newIndex);
    commit([...(autoName ? [autoName] : []), ...newRest]);
  };

  const addQuestion = (type: AddableQuestionType) => commit([...steps, makeQuestionStep(type)]);
  const addContent = () => commit([...steps, makeContentStep()]);
  const duplicate = (step: SurveyStep) => {
    const idx = steps.findIndex((s) => s.id === step.id);
    const clone = cloneStep(step);
    commit([...steps.slice(0, idx + 1), clone, ...steps.slice(idx + 1)]);
  };
  const remove = (id: string) => commit(steps.filter((s) => s.id !== id));

  return (
    <div className="space-y-3">
      {autoName && <LockedStepCard step={autoName} onEdit={() => onEditStep(autoName.id)} />}

      {rest.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center">
          <p className="text-sm font-medium text-slate-700">No questions yet</p>
          <p className="mt-1 text-sm text-slate-500">Add your first question to start building.</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={rest.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {rest.map((s) => (
                <SortableStepCard
                  key={s.id}
                  step={s}
                  onEdit={() => onEditStep(s.id)}
                  onDuplicate={() => duplicate(s)}
                  onDelete={() => remove(s.id)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <AddStepMenu onAddQuestion={addQuestion} onAddContent={addContent} />
    </div>
  );
}

function AddStepMenu({
  onAddQuestion,
  onAddContent,
}: {
  onAddQuestion: (type: AddableQuestionType) => void;
  onAddContent: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside([ref], () => setOpen(false), open);

  const pick = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={ref} className="relative">
      <Button variant="outline" onClick={() => setOpen((o) => !o)}>
        <Plus className="h-4 w-4" />
        Add question
        <ChevronDown className="h-4 w-4" />
      </Button>
      {open && (
        <div className="absolute z-10 mt-1 w-72 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
          {ADD_QUESTION_TYPES.map((t) => (
            <button
              key={t.type}
              type="button"
              onClick={() => pick(() => onAddQuestion(t.type))}
              className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              {t.label}
            </button>
          ))}
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            onClick={() => pick(onAddContent)}
            className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            Content page (no answer)
          </button>
        </div>
      )}
    </div>
  );
}
